/**
 * Admin — schools and their codes.
 *
 * One row per school: the code its students type at sign-in, the library that
 * code opens, and what those students have done with it. The numbers are the
 * same analytics rows the learner list reads, grouped by school.
 */

import { useCallback, useEffect, useState } from 'react';
import { Copy, Library, Plus, Users } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { LearnerAccessDialog } from './LearnerAccessDialog';
import {
  adminListSchools,
  adminCreateSchool,
  adminUpdateSchool,
  type AdminSchool,
} from '../../services/learnAPI';

function hours(ms: number): string {
  if (!ms) return '—';
  const minutes = Math.round(ms / 60000);
  if (minutes < 60) return `${minutes}m`;
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
}

/** Prod admin lives on app.4klabs.ai and learners on learn.4klabs.ai; every
 *  other tier serves both from one host. */
const learnOrigin = () =>
  /^app(-aws)?\.4klabs\.ai$/.test(window.location.hostname) ? 'https://learn.4klabs.ai' : window.location.origin;
const signInLink = (code: string) => `${learnOrigin()}/learn?school=${code}`;

export function SchoolsPanel({
  selectedSchoolId,
  onSelectSchool,
}: {
  selectedSchoolId: string | null;
  /** Filter the learner list below to one school; null clears it. */
  onSelectSchool: (school: AdminSchool | null) => void;
}) {
  const [schools, setSchools] = useState<AdminSchool[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [creating, setCreating] = useState(false);
  const [assigning, setAssigning] = useState<AdminSchool | null>(null);
  const [copied, setCopied] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setSchools(await adminListSchools());
      setError(null);
    } catch {
      setError('Could not load schools.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const create = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreating(true);
    setError(null);
    try {
      await adminCreateSchool({ name, ...(code.trim() ? { code } : {}) });
      setName('');
      setCode('');
      await load();
    } catch (err) {
      setError(
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
          'Could not create the school.',
      );
    } finally {
      setCreating(false);
    }
  };

  const toggleActive = async (school: AdminSchool) => {
    try {
      await adminUpdateSchool(school.id, { isActive: !school.isActive });
      await load();
    } catch {
      setError('Could not update that school.');
    }
  };

  const copy = async (school: AdminSchool) => {
    await navigator.clipboard.writeText(signInLink(school.code));
    setCopied(school.id);
    setTimeout(() => setCopied(null), 1500);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Schools</CardTitle>
        <p className="text-sm text-muted-foreground">
          A student who enters a school's code at sign-in is approved and sees that school's library.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <form onSubmit={create} className="flex flex-wrap gap-2">
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="School name"
            className="min-w-[220px] flex-1"
          />
          <Input
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder="Code (blank = generate one)"
            className="w-[240px] font-mono"
            maxLength={32}
          />
          <Button type="submit" disabled={creating || !name.trim()}>
            <Plus className="mr-1.5 h-4 w-4" /> Add school
          </Button>
        </form>

        {error && <p className="text-sm text-destructive">{error}</p>}

        {loading ? (
          <p className="py-6 text-center text-sm text-muted-foreground">Loading…</p>
        ) : schools.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">
            No schools yet. Add one, then choose its library.
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>School</TableHead>
                <TableHead>Code</TableHead>
                <TableHead>Library</TableHead>
                <TableHead className="text-right">Learners</TableHead>
                <TableHead className="text-right">Watching</TableHead>
                <TableHead className="text-right">Lessons opened</TableHead>
                <TableHead className="text-right">Completed</TableHead>
                <TableHead className="text-right">Watch time</TableHead>
                <TableHead className="text-right">Code</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {schools.map((s) => (
                <TableRow key={s.id} className={s.isActive ? undefined : 'opacity-60'}>
                  <TableCell className="font-medium">{s.name}</TableCell>
                  <TableCell>
                    <button
                      type="button"
                      onClick={() => void copy(s)}
                      title={`Copy the sign-in link: ${signInLink(s.code)}`}
                      className="inline-flex items-center gap-1.5 font-mono text-sm hover:text-primary"
                    >
                      {s.code} <Copy className="h-3.5 w-3.5" />
                      {copied === s.id && <span className="font-sans text-xs text-muted-foreground">link copied</span>}
                    </button>
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 gap-1.5 px-2 text-xs"
                      onClick={() => setAssigning(s)}
                    >
                      <Library className="h-3.5 w-3.5" />
                      {s.grantCount ? `${s.grantCount} assigned` : 'Assign…'}
                    </Button>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant={selectedSchoolId === s.id ? 'secondary' : 'ghost'}
                      size="sm"
                      className="h-7 gap-1.5 px-2 tabular-nums"
                      title="Show only this school's learners below"
                      onClick={() => onSelectSchool(selectedSchoolId === s.id ? null : s)}
                    >
                      <Users className="h-3.5 w-3.5" /> {s.learners}
                    </Button>
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{s.activeLearners || '—'}</TableCell>
                  <TableCell className="text-right tabular-nums">{s.lessonsOpened || '—'}</TableCell>
                  <TableCell className="text-right tabular-nums">{s.completes || '—'}</TableCell>
                  <TableCell className="text-right tabular-nums">{hours(s.watchMs)}</TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => void toggleActive(s)}>
                      {s.isActive ? <Badge variant="secondary">On</Badge> : <Badge variant="destructive">Off</Badge>}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
      {assigning && (
        <LearnerAccessDialog
          target="school"
          learnerId={assigning.id}
          learnerLabel={assigning.name}
          onClose={(changed) => {
            setAssigning(null);
            if (changed) void load();
          }}
        />
      )}
    </Card>
  );
}
